import { useMemo } from 'react'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'

import { usePortalQueries } from 'src/Libs/api'
import { CACHE_TIME } from 'src/Libs/installations/constants'
import { FetchedUserDataConnected } from 'src/Libs/user/models/user.model'
import { fetchHagerUserDetails } from '../api/hager-user-account-id.api'

export const hagerUserDetailsQueryKey = (username: string | undefined) => ['hager-user-details', username] as const

export type UseHagerUsersDetails = {
  /** Resolved connected user details keyed by the Hager username, `null` when it could not be resolved. */
  detailsByUsername: Map<string, FetchedUserDataConnected | null>
  isLoading: boolean
}

/**
 * Resolves multiple Hager users in parallel from their usernames to their connected user details
 * (numeric Portal user ID and contact/location data).
 *
 * For Hager users the account link `accountId` is a UUID, so the numeric user ID and contact data
 * have to be fetched from the `users` API per user. Each username is resolved in its own query so a
 * single failed or ambiguous lookup does not fail the others; unresolved usernames map to `null`.
 *
 * @param usernames - Hager usernames to resolve
 */
export const useHagerUsersDetails = (usernames: string[]): UseHagerUsersDetails => {
  const authFetch = useAuthFetchWithResponse()

  const results = usePortalQueries<FetchedUserDataConnected | null>(
    usernames.map(username => ({
      queryKey: hagerUserDetailsQueryKey(username),
      queryFn: () => fetchHagerUserDetails(username, authFetch),
      enabled: !!username,
      staleTime: CACHE_TIME,
    }))
  )

  const detailsByUsername = useMemo(() => {
    const map = new Map<string, FetchedUserDataConnected | null>()
    usernames.forEach((username, index) => {
      map.set(username, results[index]?.data ?? null)
    })

    return map
  }, [usernames, results])

  const isLoading = results.some(result => result.isLoading)

  return { detailsByUsername, isLoading }
}
